import { cn } from "@/common/helpers/utils";
import { useDevice } from "@/common/hooks/use-device";
import { DataTableItemsCount } from "@/components/data-table/_components/data-table-items-count";
import { DataTablePagination } from "@/components/data-table/_components/data-table-pagination";
import { FilterList, FilterListProps } from "@/components/filter/_components/filter-list";
import { GroupedFilterList } from "@/components/filter/_components/grouped-filter-list";
import { Filter } from "@/components/filter/_types";
import { FilterContainer } from "@/components/filter/filter-container";
import { ResponsiveSearch } from "@/components/search-input/responsive-search";
import { RowData } from "@tanstack/react-table";
import { ReactNode } from "react";
import { RowWithIdentifier } from "../_helpers/row-identifier";
import {
  DataTableInstance,
  FilterContainerCustomContentPosition,
  SortColumn,
  TableBackground,
  TableFeatures,
  TimestampSortField,
  tableBackgroundStyles,
} from "../types";
import { DataTableSortMenu } from "./data-table-sort-menu";

interface DataTableHeaderProps<TData extends RowData & RowWithIdentifier> {
  headerComponent?: ReactNode;
  filterContainerClassName?: string;
  filterContainerCustomContent?: ReactNode;
  filterContainerCustomContentPosition?: FilterContainerCustomContentPosition;
  features: TableFeatures;
  filters: Filter<unknown, unknown>[];
  activeFilterCount: number;
  hasDefaultFilters: boolean;
  hasNonDefaultFilters: boolean;
  searchTerm: string;
  searchPlaceholder: string;
  table: DataTableInstance<TData>;
  sortColumns: SortColumn[];
  timestampSortFields: TimestampSortField[];
  serverSidePagination: boolean;
  totalItems?: number;
  isLoading?: boolean;
  onFilterChange: (filterId: string, values: string[]) => void;
  onClearAllFilters?: () => void;
  onResetAllFilters?: () => void;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  tableBackground?: TableBackground;
  currentFilterValues: Record<string, string[]>;
  SearchComponent?: React.ComponentType<{
    placeholder?: string;
    value?: string;
    onSearch?: (value: string) => void;
    onSearchChange?: (value: string) => void;
    className?: string;
  }>;
}

export function DataTableHeader<TData extends RowData & RowWithIdentifier>({
  headerComponent,
  filterContainerClassName,
  filterContainerCustomContent,
  filterContainerCustomContentPosition = FilterContainerCustomContentPosition.Top,
  features,
  filters,
  activeFilterCount,
  hasDefaultFilters,
  hasNonDefaultFilters,
  searchTerm,
  searchPlaceholder,
  table,
  sortColumns,
  timestampSortFields,
  serverSidePagination,
  totalItems,
  isLoading,
  onFilterChange,
  onClearAllFilters,
  onResetAllFilters,
  onSearchChange,
  onClearSearch,
  tableBackground,
  currentFilterValues,
  SearchComponent,
}: DataTableHeaderProps<TData>) {
  const { isMobile } = useDevice();
  const itemsCount = serverSidePagination ? (totalItems ?? 0) : table.getFilteredRowModel().rows.length;
  const isCountLoading = serverSidePagination ? totalItems === undefined : isLoading === true;

  const hasGroups = filters.some((filter) => filter.group);

  const renderFilters = () => {
    if (filters.length === 0) return null;

    const filterListProps: FilterListProps = { filters, currentFilterValues, onFilterChange };

    return (
      <FilterContainer
        triggerLabel="Filters"
        className={filterContainerClassName}
        activeFilterCount={activeFilterCount}
        hasDefaultFilters={hasDefaultFilters}
        hasNonDefaultFilters={hasNonDefaultFilters}
        onClearAll={onClearAllFilters}
        onResetAll={onResetAllFilters}
      >
        <div className="space-y-4">
          {filterContainerCustomContent &&
            filterContainerCustomContentPosition === FilterContainerCustomContentPosition.Top && (
              <div className="mb-4 flex flex-col gap-4">{filterContainerCustomContent}</div>
            )}
          {hasGroups ? <GroupedFilterList {...filterListProps} /> : <FilterList {...filterListProps} />}
          {filterContainerCustomContent &&
            filterContainerCustomContentPosition === FilterContainerCustomContentPosition.Bottom && (
              <div className="mt-4 flex flex-col gap-4">{filterContainerCustomContent}</div>
            )}
        </div>
      </FilterContainer>
    );
  };

  return (
    <>
      {headerComponent && isMobile && (
        <div
          className={cn(
            "border-border relative z-50 flex shrink-0 items-center gap-2 backdrop-blur-sm lg:gap-3",
            tableBackgroundStyles[tableBackground ?? TableBackground.Background],
          )}
        >
          {headerComponent}
        </div>
      )}
      <div
        className={cn(
          "border-border relative flex shrink-0 flex-wrap items-center gap-2 py-2 backdrop-blur-sm lg:gap-3",
          tableBackgroundStyles[tableBackground ?? TableBackground.Background],
        )}
      >
        {headerComponent && !isMobile && (
          <div className="flex shrink-0 items-center gap-2 lg:gap-3">{headerComponent}</div>
        )}

        <div className="flex min-w-0 flex-1 items-center justify-end gap-2 overflow-visible lg:gap-3">
          {renderFilters()}
          {(sortColumns.length > 0 || timestampSortFields.length > 0) && (
            <DataTableSortMenu table={table} sortColumns={sortColumns} timestampSortFields={timestampSortFields} />
          )}
          {features.search &&
            (SearchComponent ? (
              <SearchComponent
                placeholder={searchPlaceholder}
                value={searchTerm}
                onSearch={onSearchChange}
                onSearchChange={onSearchChange}
              />
            ) : (
              <ResponsiveSearch
                placeholder={searchPlaceholder}
                onSearchChange={onSearchChange}
                onChange={onSearchChange}
                onClear={onClearSearch}
                value={searchTerm}
              />
            ))}
          <DataTableItemsCount count={itemsCount} isLoading={isCountLoading} />
          {features.pagination && (
            <DataTablePagination table={table} itemsCount={itemsCount} isLoading={isCountLoading} />
          )}
        </div>
      </div>
    </>
  );
}
